import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { getTabbables } from '../../utils/getTabbables';
import { Icon } from '../Icon';

type DialogRootContextValue = {
  modal: boolean;
  openerRef: React.RefObject<HTMLElement | null>;
};

const DialogRootContext = React.createContext<DialogRootContextValue | null>(null);

type DialogContentContextValue = {
  titleRef: React.RefObject<HTMLElement | null>;
  setDescriptionMounted: (mounted: boolean) => void;
};

const DialogContentContext = React.createContext<DialogContentContextValue | null>(null);

function getFocusedOpener(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) (ref as React.RefObject<T | null>).current = value;
}

function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const isOpen = isControlled ? open : uncontrolledOpen;
  const openerRef = React.useRef<HTMLElement | null>(null);
  const prevOpenRef = React.useRef(false);

  // Layout effect because Radix moves focus into the content in a passive effect.
  React.useLayoutEffect(() => {
    if (isOpen && !prevOpenRef.current) openerRef.current = getFocusedOpener();
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // Radix runs controlled because its uncontrolled onOpenChange fires after focus has already moved.
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const contextValue = React.useMemo(() => ({ modal, openerRef }), [modal]);

  return (
    <DialogRootContext.Provider value={contextValue}>
      <DialogPrimitive.Root data-slot="dialog" open={isOpen} onOpenChange={handleOpenChange} modal={modal} {...props} />
    </DialogRootContext.Provider>
  );
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/50',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

type DialogContentPrimitiveProps = React.ComponentProps<typeof DialogPrimitive.Content>;

type TestIdProps = { 'data-testid'?: string };

type DialogContentProps = Omit<DialogContentPrimitiveProps, 'role' | 'title'> & {
  overlayClassName?: string;
  role?: 'dialog' | 'alertdialog';
  title: Exclude<React.ReactNode, null | undefined | boolean>;
  description?: React.ReactNode;
  preventDismiss?: boolean;
  dismissible?: boolean;
  headerProps?: React.ComponentProps<'div'> & TestIdProps;
  titleProps?: Omit<React.ComponentProps<typeof DialogPrimitive.Title>, 'children'> & TestIdProps;
  closeButtonProps?: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close> & TestIdProps;
};

function DialogContent({
  className,
  children,
  overlayClassName,
  role = 'dialog',
  title,
  description,
  preventDismiss = false,
  dismissible = true,
  headerProps,
  titleProps,
  closeButtonProps,
  onOpenAutoFocus,
  onCloseAutoFocus,
  onEscapeKeyDown,
  onInteractOutside,
  onPointerDownOutside,
  onKeyDown,
  ...props
}: DialogContentProps) {
  const rootContext = React.useContext(DialogRootContext);
  const modal = rootContext?.modal ?? true;
  const titleRef = React.useRef<HTMLElement | null>(null);
  const interactedOutsideRef = React.useRef(false);
  const [descriptionMounted, setDescriptionMounted] = React.useState(false);
  const contentContextValue = React.useMemo(() => ({ titleRef, setDescriptionMounted }), []);
  const blockDismiss = preventDismiss || !dismissible;
  // Typed callers cannot pass an empty title; this still guards untyped ones.
  const titleValue: unknown = title;
  const hasTitle = titleValue !== undefined && titleValue !== null && titleValue !== false;
  const hasDescription = description !== undefined && description !== null && description !== false;
  const contentTestId = (props as { 'data-testid'?: string })['data-testid'];
  const isBlankTitle = typeof title === 'string' && title.trim() === '';

  // Radix only warns when no title element exists; a blank string renders one that names nothing.
  React.useEffect(() => {
    if (isBlankTitle)
      console.error('`DialogContent` requires a non-empty `title` to name the dialog for screen readers.');
  }, [isBlankTitle]);

  const handleOpenAutoFocus = (event: Event) => {
    onOpenAutoFocus?.(event);
    if (event.defaultPrevented) return;
    const titleNode = titleRef.current;
    if (!titleNode) return;
    titleNode.focus({ preventScroll: true });
    if (document.activeElement === titleNode) event.preventDefault();
  };

  // Leaves default unprevented so Radix's handler still moves focus to a DialogTrigger when one exists.
  const handleCloseAutoFocus = (event: Event) => {
    onCloseAutoFocus?.(event);
    const interactedOutside = interactedOutsideRef.current;
    interactedOutsideRef.current = false;
    if (event.defaultPrevented) return;
    if (!modal && interactedOutside) return;
    const opener = rootContext?.openerRef.current;
    if (!opener?.isConnected) return;
    const container = event.currentTarget instanceof Node ? event.currentTarget : null;
    if (container?.contains(opener)) return;
    const active = document.activeElement;
    if (active && active !== document.body) return;
    opener.focus();
  };

  // The heading sits outside Radix's tab loop, so Shift+Tab from it wraps to the last control by hand.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented || event.key !== 'Tab' || !event.shiftKey) return;
    const titleNode = titleRef.current;
    if (!titleNode || document.activeElement !== titleNode) return;
    const tabbables = getTabbables(event.currentTarget);
    const first = tabbables[0];
    const last = tabbables[tabbables.length - 1];
    if (!first || !last) return;
    if (!(titleNode.compareDocumentPosition(first) & Node.DOCUMENT_POSITION_FOLLOWING)) return;
    event.preventDefault();
    last.focus();
  };

  const handleEscapeKeyDown: DialogContentPrimitiveProps['onEscapeKeyDown'] = (event) => {
    onEscapeKeyDown?.(event);
    if (blockDismiss) event.preventDefault();
  };

  const handlePointerDownOutside: DialogContentPrimitiveProps['onPointerDownOutside'] = (event) => {
    onPointerDownOutside?.(event);
    if (blockDismiss) event.preventDefault();
  };

  const handleInteractOutside: DialogContentPrimitiveProps['onInteractOutside'] = (event) => {
    onInteractOutside?.(event);
    if (blockDismiss) event.preventDefault();
    if (!event.defaultPrevented) interactedOutsideRef.current = true;
  };

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogContentContext.Provider value={contentContextValue}>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            'fixed left-[50%] top-[50%] z-50 flex flex-col w-full max-w-lg max-h-[80vh] translate-x-[-50%] translate-y-[-50%] overflow-hidden',
            'bg-white border border-grey-30 rounded-lg shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'duration-200',
            className,
          )}
          {...(descriptionMounted ? {} : { 'aria-describedby': undefined })}
          {...props}
          role={role}
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          onEscapeKeyDown={handleEscapeKeyDown}
          onPointerDownOutside={handlePointerDownOutside}
          onInteractOutside={handleInteractOutside}
          onKeyDown={handleKeyDown}
        >
          {hasTitle && (
            <DialogHeader {...headerProps}>
              <DialogTitle {...titleProps} className={cn('pr-8', titleProps?.className)}>
                {title}
              </DialogTitle>
              {dismissible && (
                <DialogCloseButton
                  data-testid={contentTestId ? `${contentTestId}-close` : undefined}
                  {...closeButtonProps}
                />
              )}
            </DialogHeader>
          )}
          {hasDescription && <DialogDescription className="px-4">{description}</DialogDescription>}
          {children}
        </DialogPrimitive.Content>
      </DialogContentContext.Provider>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex items-center justify-between px-4 py-4', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ref, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  const contentContext = React.useContext(DialogContentContext);
  const nodeRef = React.useRef<HTMLHeadingElement | null>(null);

  const setRef = React.useCallback(
    (node: HTMLHeadingElement | null) => {
      const titleRef = contentContext?.titleRef;
      if (titleRef) {
        if (node) titleRef.current = node;
        else if (titleRef.current === nodeRef.current) titleRef.current = null;
      }
      nodeRef.current = node;
      assignRef(ref, node);
    },
    [contentContext, ref],
  );

  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      tabIndex={-1}
      className={cn('text-lg font-semibold text-primary', 'outline-none', className)}
      {...props}
      ref={setRef}
    />
  );
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  const setDescriptionMounted = React.useContext(DialogContentContext)?.setDescriptionMounted;

  // Radix links aria-describedby to the description id even when none renders, so the content drops it until one mounts.
  React.useLayoutEffect(() => {
    setDescriptionMounted?.(true);
    return () => setDescriptionMounted?.(false);
  }, [setDescriptionMounted]);

  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-grey-60', className)}
      {...props}
    />
  );
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="dialog-body" className={cn('flex-1 min-h-0 overflow-y-auto px-4 py-4', className)} {...props} />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex items-center justify-end gap-2 px-4 py-4', className)}
      {...props}
    />
  );
}

function DialogCloseButton({ className, ...props }: React.ComponentPropsWithoutRef<typeof DialogClose>) {
  return (
    <DialogClose
      className={cn(
        'absolute top-4 right-4 w-5 h-5 flex items-center justify-center rounded-[6px] opacity-70 transition-opacity cursor-pointer hover:opacity-100 focus-ring disabled:pointer-events-none',
        className,
      )}
      {...props}
    >
      <Icon icon="cross2" size={20} className="text-primary-dark" />
      <span className="sr-only">Close</span>
    </DialogClose>
  );
}

export type { DialogContentProps };

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
  DialogTrigger,
};
